
import React, { ReactNode } from 'react';
import { Home, List, Shield, BookOpen, Sun, Moon, HandHeart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { UserProfile } from '../types';

interface LayoutProps {
  children: ReactNode;
  userProfile: UserProfile;
  isDark: boolean;
  toggleTheme: () => void;
}

// REORDERED NAV: Hub, Read, Service, Sponsor, Profile (handled in bottom bar logic or separate)
const NAV_ITEMS = [
  { icon: Home, label: 'Hub', path: '/' },
  { icon: BookOpen, label: 'Read', path: '/read' },
  { icon: HandHeart, label: 'Service', path: '/service' },
  { icon: Shield, label: 'Sponsor', path: '/sponsor' },
];

export const Layout: React.FC<LayoutProps> = ({ children, userProfile, isDark, toggleTheme }) => {
  const location = useLocation();

  // Generate Initials
  const initials = `${userProfile.firstName.charAt(0)}${userProfile.lastInitial}`.toUpperCase();
  const fullName = `${userProfile.firstName} ${userProfile.lastInitial}.`;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
      {/* Desktop Sidebar (Nav Rail) */}
      <aside className="hidden md:flex flex-col w-20 lg:w-64 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 fixed h-full z-20 transition-all duration-300">
        
        {/* Branding Header (Top) */}
        <div className="p-6 flex items-center justify-center lg:justify-start gap-3 border-b border-slate-100 dark:border-slate-800">
          <div className="w-8 h-8 bg-brand-600 rounded-lg flex-shrink-0 flex items-center justify-center text-white font-bold text-lg">R</div>
          <span className="font-bold text-xl tracking-tight hidden lg:block dark:text-white">Reprieve</span>
        </div>
        
        {/* Navigation (Middle) */}
        <nav className="flex-1 mt-6 px-4 space-y-2">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${
                location.pathname === item.path 
                  ? 'bg-brand-50 dark:bg-slate-800 text-brand-600 dark:text-brand-400 font-medium' 
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <item.icon className="w-6 h-6" />
              <span className="hidden lg:block">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* User Profile & Settings (Bottom) */}
        <div className="p-4 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between p-2 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700">
                <Link to="/profile" className="flex items-center gap-3 overflow-hidden">
                    <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900 text-brand-600 dark:text-brand-300 flex-shrink-0 flex items-center justify-center font-bold text-xs border border-brand-200 dark:border-brand-700">
                        {initials || 'ME'}
                    </div>
                    <div className="hidden lg:block overflow-hidden">
                        <h3 className="font-bold text-slate-800 dark:text-slate-100 text-xs truncate">{fullName}</h3>
                        <p className="text-[10px] text-slate-500">Settings</p>
                    </div>
                </Link>
                <button 
                    onClick={toggleTheme}
                    className="text-slate-400 hover:text-brand-600 dark:hover:text-brand-400 p-1.5 rounded-lg hover:bg-white dark:hover:bg-slate-700 transition-colors"
                    title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                >
                    {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>
            </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-20 lg:ml-64 pb-24 md:pb-0">
        <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 z-50 px-6 py-3 flex justify-between items-center safe-area-bottom">
        {NAV_ITEMS.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex flex-col items-center gap-1 ${
               location.pathname === item.path ? 'text-brand-600 dark:text-brand-400' : 'text-slate-400'
            }`}
          >
            <item.icon className="w-6 h-6" />
            <span className="text-[10px] font-medium">{item.label}</span>
          </Link>
        ))}
         <Link to="/profile" className={`flex flex-col items-center gap-1 ${location.pathname === '/profile' ? 'text-brand-600' : 'text-slate-400'}`}>
             <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold">
                 {initials}
             </div>
             <span className="text-[10px] font-medium">Profile</span>
         </Link>
      </div>
    </div>
  );
};
